import { ErrorCodes } from '@/errors'
import { sessionRecorder } from '@/services/sessionRecorder'

// Estado global do modal de erro
let globalVueApp = null

/**
 * Registra a instância da aplicação Vue
 */
export function registerVueApp(app) {
  globalVueApp = app
}

/**
 * Mostra o modal de erro com dados específicos
 */
export function showErrorModal(errorData) {
  console.error('Unexpected error:', errorData)

  // Verifica se o erro já foi registrado na sessão para evitar duplicidade
  const errorMsg = errorData.error || errorData.message
  const alreadyExists = sessionRecorder.errors.some(e => e.message === errorMsg || e.error === errorMsg)

  if (!alreadyExists) {
    sessionRecorder.addError(errorData)
  }
}

/**
 * Processa erros de resposta da API e exibe modal apropriado
 */
export async function handleApiError(error, context = {}) {
  const errorData = processApiError(error, context)

  // Casos especiais que requerem redirecionamento
  const wasHandled = await handleSpecialCases(errorData)
  if (wasHandled) {
    return
  }

  // Mostra modal para outros erros
  showErrorModal(errorData)
}

/**
 * Processa erro da API e extrai informações relevantes
 */
function processApiError(error, context) {
  let errorData = {
    code: null,
    error: 'Erro inesperado',
    details: {},
    originalError: error
  }

  // Se o erro tem uma resposta da API
  if (error.response && error.response.data) {
    const responseData = error.response.data

    errorData.code = responseData.code
    errorData.error = responseData.error || responseData.message

    // Adiciona detalhes extras se disponíveis
    if (responseData.extra) {
      errorData.details = {
        ...errorData.details,
        ...responseData.extra
      }
    }

    // Adiciona informações de terminal se disponíveis
    if (responseData.terminal) {
      errorData.details.terminal_info = responseData.terminal
    }

    // Adiciona ID da transação se disponível
    if (responseData.transaction_id) {
      errorData.details.transaction_id = responseData.transaction_id
    }
  }
  // Se é um erro de rede ou outro tipo
  else if (error.message) {
    errorData.error = error.message
  }

  // Adiciona contexto fornecido
  errorData.details = {
    ...errorData.details,
    ...context,
    timestamp: new Date().toISOString()
  }

  return errorData
}

/**
 * Trata casos especiais que requerem ações específicas
 */
async function handleSpecialCases(errorData) {
  // Redirecionamento para logout em caso de não autorizado
  if (
    (errorData.originalError?.status === 401 || errorData.originalError?.response?.status === 401) &&
    !errorData.originalError?.skipRedirect
  ) {
    try {
      const { default: router } = await import('@/router')
      router.push({ name: 'logout' })
      return true
    } catch (error) {
      console.error('Erro ao importar router:', error)
      // Fallback para redirecionamento direto
      window.location.href = '/logout'
      return true
    }
  }

  // Outros casos especiais podem ser adicionados aqui

  return false
}

/**
 * Função principal para tratamento de erros (compatibilidade com código existente)
 */
export default function handleError(error, context = {}) {
  console.error('Error handled:', error)

  // Captura exceção para análise
  if (globalVueApp && globalVueApp.config && globalVueApp.config.globalProperties.$captureException) {
    globalVueApp.config.globalProperties.$captureException(error, context)
  }

  // Processa e exibe erro
  handleApiError(error, context)
}

/**
 * Tratamento específico para erros de validação
 */
export function handleValidationError(validationResponse, context = {}) {
  const errorData = {
    code: validationResponse.code,
    error: validationResponse.error,
    details: {
      ...context,
      timestamp: new Date().toISOString()
    }
  }

  // Adiciona informações extras se disponíveis
  if (validationResponse.extra) {
    errorData.details = { ...errorData.details, ...validationResponse.extra }
  }

  if (validationResponse.cable_type_available) {
    errorData.details.cable_type_available = validationResponse.cable_type_available
  }

  if (validationResponse.transaction_id) {
    errorData.details.transaction_id = validationResponse.transaction_id
  }

  showErrorModal(errorData)
}

/**
 * Mapear códigos de erro para tipos conhecidos
 */
export function getErrorType(errorCode) {
  const errorTypes = {
    [ErrorCodes.VALIDATION_ERROR]: 'validation',
    [ErrorCodes.TERMINAL_OFFLINE]: 'terminal_offline',
    [ErrorCodes.INVALID_TOKEN]: 'invalid_token',
    [ErrorCodes.TERMINAL_NOT_FOUND]: 'terminal_not_found',
    [ErrorCodes.NO_BATTERY_WITH_CABLE]: 'no_cable',
    [ErrorCodes.NO_CHARGED_BATTERY]: 'no_charged',
    [ErrorCodes.NO_PAYMENT_TYPE]: 'no_payment',
    [ErrorCodes.EMAIL_ALREADY_REGISTERED]: 'email_exists',
    [ErrorCodes.REGISTER_CODE_INVALID]: 'invalid_code',
    [ErrorCodes.INVALID_EMAIL]: 'invalid_email',
    [ErrorCodes.TOO_SHORT_PASSWORD]: 'weak_password'
  }

  return errorTypes[errorCode] || 'generic'
}

/**
 * Verificar se um erro pode ser tentado novamente
 */
export function canRetryError(errorCode) {
  const retryableErrors = [
    ErrorCodes.TERMINAL_OFFLINE,
    ErrorCodes.REQUEST_IN_PROGRESS,
    ErrorCodes.INVALID_TOKEN,
    ErrorCodes.NO_CHARGED_BATTERY,
    ErrorCodes.REGISTER_CODE_INVALID
  ]

  return retryableErrors.includes(errorCode)
}

/**
 * Verificar se um erro permite mudança de cabo
 */
export function canChangeCable(errorCode) {
  return errorCode === ErrorCodes.NO_BATTERY_WITH_CABLE
}

/**
 * Verificar se um erro permite seleção de outro terminal
 */
export function canSelectOtherTerminal(errorCode) {
  const terminalRelatedErrors = [
    ErrorCodes.TERMINAL_OFFLINE,
    ErrorCodes.NO_CHARGED_BATTERY,
    ErrorCodes.NO_BATTERY_WITH_CABLE
  ]

  return terminalRelatedErrors.includes(errorCode)
}
